import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Download, Users, Package, ChartLineUp, CreditCard, ShieldWarning, ArrowsDownUp, Envelope } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

var reportTypes = [
  { id: 'affiliates', label: 'Affiliates', desc: 'All affiliates with earnings, clicks, conversions', icon: Users, color: 'text-blue-400' },
  { id: 'offers', label: 'Offers', desc: 'All offers with performance metrics', icon: Package, color: 'text-amber-400' },
  { id: 'revenue', label: 'Revenue', desc: 'Daily revenue, clicks, conversions, EPC', icon: ChartLineUp, color: 'text-emerald-400' },
  { id: 'traffic', label: 'Traffic', desc: 'Traffic by date and source', icon: ChartLineUp, color: 'text-blue-400' },
  { id: 'payouts', label: 'Payouts', desc: 'All payout transactions', icon: CreditCard, color: 'text-emerald-400' },
  { id: 'fraud', label: 'Fraud Alerts', desc: 'Fraud detection alerts and scores', icon: ShieldWarning, color: 'text-red-400' },
  { id: 'commissions', label: 'Commissions', desc: 'Sub-affiliate commission waterfall log', icon: ArrowsDownUp, color: 'text-purple-400' },
];

export default function ReportCenter() {
  var [downloading, setDownloading] = useState(null);
  var [emailStatus, setEmailStatus] = useState(null);

  useEffect(function () {
    api.get('/email/status').then(function (r) { setEmailStatus(r.data); }).catch(function () {});
  }, []);

  var downloadReport = async function (reportId, format) {
    var key = reportId + '-' + format;
    setDownloading(key);
    try {
      var url = '/reports/' + reportId + (format === 'pdf' ? '/pdf' : '');
      var response = await api.get(url, { responseType: 'blob' });
      var blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', reportId + '_report.' + format);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(reportId + ' ' + format.toUpperCase() + ' downloaded');
    } catch (err) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="report-center">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <FileText size={32} className="text-blue-400" weight="duotone" />
          Report Center
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Export comprehensive CSV and PDF reports across all data types</p>
      </div>

      {emailStatus && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center gap-3">
            <Envelope size={20} className={emailStatus.provider === 'sendgrid' ? 'text-emerald-400' : 'text-amber-400'} />
            <div className="flex-1">
              <span className="text-sm text-zinc-200">Email System: <Badge className={(emailStatus.provider === 'sendgrid' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400') + ' text-[10px] ml-2'}>{emailStatus.mode}</Badge></span>
              <p className="text-xs text-zinc-500 mt-0.5">{emailStatus.total_emails} emails logged, {emailStatus.sent_emails} sent via provider</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {reportTypes.map(function (report) {
          var Icon = report.icon;
          return (
            <Card key={report.id} className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all" data-testid={'report-card-' + report.id}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <Icon size={24} className={report.color} weight="duotone" />
                  <div className="flex gap-1">
                    <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">CSV</Badge>
                    <Badge className="bg-blue-600/20 text-blue-400 text-[10px]">PDF</Badge>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-white text-base mb-1">{report.label}</h3>
                <p className="text-xs text-zinc-400 mb-4">{report.desc}</p>
                <div className="flex gap-2">
                  <Button
                    data-testid={'download-csv-' + report.id}
                    onClick={function () { downloadReport(report.id, 'csv'); }}
                    disabled={downloading === report.id + '-csv'}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 gap-1.5 text-xs"
                    size="sm"
                  >
                    {downloading === report.id + '-csv' ? <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <Download size={12} />}
                    CSV
                  </Button>
                  <Button
                    data-testid={'download-pdf-' + report.id}
                    onClick={function () { downloadReport(report.id, 'pdf'); }}
                    disabled={downloading === report.id + '-pdf'}
                    className="flex-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/20 gap-1.5 text-xs"
                    size="sm"
                  >
                    {downloading === report.id + '-pdf' ? <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <FileText size={12} />}
                    PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
